import React, { useEffect, useState } from 'react';
import { Phone, Info, X, Crown } from 'lucide-react';
import { AgentFilters } from '../utils/agentMatrix';
import { useUser } from '../contexts/UserContext';
interface MainTrainingCardProps {
  onStartCall: () => void;
  filters: AgentFilters;
  onToggleFilters: () => void;
  showFilters: boolean;
  isAdminUnlocked: boolean;
}
interface TopRepBadgeProps {
  rank: 1 | 2 | 3;
}
function TopRepBadge({
  rank
}: TopRepBadgeProps) {
  const colors = {
    1: 'bg-gradient-to-br from-yellow-400 to-yellow-600',
    2: 'bg-gradient-to-br from-gray-300 to-gray-400',
    3: 'bg-gradient-to-br from-blue-400 to-blue-600'
  };
  return <div className="relative w-16 h-16 flex-shrink-0" data-id="element-330">
      <div className={`w-16 h-16 ${colors[rank]} rounded-full flex items-center justify-center shadow-lg`} data-id="element-331">
        <span className="text-white text-2xl font-bold" data-id="element-332">{rank}</span>
      </div>
      <div className="absolute -top-1 left-1/2 -translate-x-1/2" data-id="element-333">
        <Crown className="w-6 h-6 text-yellow-500 fill-yellow-400" data-id="element-334" />
      </div>
    </div>;
}
export function MainTrainingCard({
  onStartCall,
  isAdminUnlocked
}: MainTrainingCardProps) {
  const {
    currentUser
  } = useUser();
  const [selectedCallType, setSelectedCallType] = useState<'inbound' | 'outbound' | null>(null);
  const [showInfoNotification, setShowInfoNotification] = useState(false);
  const [showSelectTypePopup, setShowSelectTypePopup] = useState(false);
  // Auto-dismiss inbound locked notification after 5 seconds
  useEffect(() => {
    if (showInfoNotification) {
      const timer = setTimeout(() => {
        setShowInfoNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showInfoNotification]);
  // Auto-dismiss select type notification after 5 seconds
  useEffect(() => {
    if (showSelectTypePopup) {
      const timer = setTimeout(() => {
        setShowSelectTypePopup(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSelectTypePopup]);
  const handleInboundClick = () => {
    if (!isAdminUnlocked) {
      setShowInfoNotification(true);
    } else {
      // Toggle: if already selected, unselect it
      setSelectedCallType(prev => prev === 'inbound' ? null : 'inbound');
    }
  };
  const handleOutboundClick = () => {
    // Toggle: if already selected, unselect it
    setSelectedCallType(prev => prev === 'outbound' ? null : 'outbound');
  };
  const handleStartCall = () => {
    if (!selectedCallType) {
      setShowSelectTypePopup(true);
    } else {
      onStartCall();
    }
  };
  return <>
      <div className="w-full px-6 pb-8" data-id="element-335">
        {/* Hero Profile Section */}
        <div className="flex flex-col items-center mb-8" data-id="element-336">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-337">
            {/* Top Rep Badge - Left of Name */}
            {currentUser?.topRepRank && <TopRepBadge rank={currentUser.topRepRank} data-id="element-338" />}

            <h1 className="text-5xl font-black text-black tracking-tight" data-id="element-339">
              {currentUser?.name.toUpperCase()}
            </h1>
          </div>

          <div className="flex items-center gap-4 mb-2" data-id="element-340">
            {/* Profile Image */}
            <div className="w-24 h-24 rounded-full bg-gray-300 overflow-hidden border-4 border-white shadow-lg" data-id="element-341">
              <img src={currentUser?.avatar} alt={currentUser?.name} className="w-full h-full object-cover" data-id="element-342" />
            </div>

            <div className="flex flex-col items-start" data-id="element-343">
              <div className="flex items-baseline gap-2" data-id="element-344">
                <span className="text-3xl font-bold text-green-600" data-id="element-345">
                  {currentUser?.averageScore.toFixed(1) || '0.0'}
                </span>
                <span className="text-xl font-semibold text-gray-700" data-id="element-346">
                  Points
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm font-medium" data-id="element-347">
                <div className="flex items-center gap-1" data-id="element-348">
                  <span className="text-orange-500" data-id="element-349">
                    {currentUser?.totalCalls || 0}
                  </span>
                  <span className="text-gray-600" data-id="element-350">Calls</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-351">
                  <span className="text-purple-600" data-id="element-352">0min</span>
                  <span className="text-gray-600" data-id="element-353">Talk Time</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Action Pill */}
        <div className={`relative w-full h-20 rounded-full flex items-center justify-between px-6 shadow-xl transition-all duration-500 ${selectedCallType === 'outbound' ? 'bg-gradient-to-l from-green-500 via-green-600/50 to-black' : selectedCallType === 'inbound' ? 'bg-gradient-to-r from-blue-500 via-blue-600/50 to-black' : 'bg-black'}`} data-id="element-354">
          <button onClick={handleInboundClick} className="flex items-center gap-1.5 text-white/90 pl-2 hover:text-white transition-colors z-20" data-id="element-355">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-356">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" data-id="element-357"></path>
            </svg>
            <span className="text-sm font-medium" data-id="element-358">Inbound</span>
          </button>

          {/* Central Call Button */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10" data-id="element-359">
            <button onClick={handleStartCall} className="relative w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-lg border-[6px] border-[#eef0f5] group active:scale-95 transition-all duration-300 glowing-box-active" data-id="element-360">
              {/* Glowing Ring Animation - On Outer Circle */}
              <div className="glowing-box-animations" data-id="element-361">
                <div className="glowing-box-borders-masker" data-id="element-362">
                  <div className="glowing-box-borders absolute inset-0" data-id="element-363"></div>
                </div>
                <div className="glowing-box-borders-masker" data-id="element-364">
                  <div className="glowing-box-glow absolute inset-0" data-id="element-365"></div>
                </div>
              </div>

              <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 relative ${selectedCallType === 'outbound' ? 'bg-gradient-to-br from-green-400 to-green-600' : selectedCallType === 'inbound' ? 'bg-gradient-to-br from-blue-400 to-blue-600' : 'bg-gradient-to-br from-blue-100/40 via-purple-100/40 to-pink-100/40'}`} style={!selectedCallType ? {
              background: 'radial-gradient(circle at 30% 30%, rgba(219, 234, 254, 0.6), rgba(233, 213, 255, 0.5), rgba(252, 231, 243, 0.5), rgba(254, 249, 195, 0.4))'
            } : undefined} data-id="element-366">
                <Phone className={`w-8 h-8 fill-current transition-colors duration-500 ${selectedCallType === 'outbound' || selectedCallType === 'inbound' ? 'text-white' : 'text-black'}`} data-id="element-367" />
              </div>
            </button>
          </div>

          <button onClick={handleOutboundClick} className="flex items-center gap-1.5 text-white/90 pr-2 hover:text-white transition-colors z-20" data-id="element-368">
            <span className="text-sm font-medium" data-id="element-369">Outbound</span>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-370">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" data-id="element-371"></path>
            </svg>
          </button>
        </div>
      </div>

      {/* Info Notification - Inbound Locked (slides down from top) */}
      {showInfoNotification && <div className="fixed top-20 left-0 right-0 z-50 px-6 animate-in slide-in-from-top-4 fade-in duration-300" data-id="element-372">
          <div className="max-w-[400px] mx-auto bg-gradient-to-br from-blue-50 to-blue-100/95 border-2 border-blue-300 rounded-2xl p-4 flex items-start gap-3 shadow-2xl backdrop-blur-sm" data-id="element-373">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg" data-id="element-374">
              <Info className="w-5 h-5 text-white" data-id="element-375" />
            </div>
            <div className="flex-1 pt-0.5" data-id="element-376">
              <p className="text-sm font-semibold text-blue-900 leading-snug" data-id="element-377">
                To unlock inbound calls, please login to Admin mode first.
              </p>
            </div>
            <button onClick={() => setShowInfoNotification(false)} className="text-blue-400 hover:text-blue-600 transition-colors flex-shrink-0" data-id="element-378">
              <X className="w-5 h-5" data-id="element-379" />
            </button>
          </div>
        </div>}

      {/* Select Call Type Notification - Slide down from top with auto-dismiss */}
      {showSelectTypePopup && <div className="fixed top-20 left-0 right-0 z-50 px-6 animate-in slide-in-from-top-4 fade-in duration-300" data-id="element-380">
          <div className="max-w-[400px] mx-auto bg-gradient-to-br from-blue-50 to-blue-100/95 border-2 border-blue-300 rounded-2xl p-4 flex items-start gap-3 shadow-2xl backdrop-blur-sm" data-id="element-381">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg" data-id="element-382">
              <Info className="w-5 h-5 text-white" data-id="element-383" />
            </div>
            <div className="flex-1 pt-0.5" data-id="element-384">
              <p className="text-sm font-semibold text-blue-900 leading-snug" data-id="element-385">
                Please select Inbound or Outbound calling before dialing a lead.
              </p>
            </div>
            <button onClick={() => setShowSelectTypePopup(false)} className="text-blue-400 hover:text-blue-600 transition-colors flex-shrink-0" data-id="element-386">
              <X className="w-5 h-5" data-id="element-387" />
            </button>
          </div>
        </div>}
    </>;
}